import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  getDirectoryUsersByIdsOptions,
  getInfiniteUserDirectoryOptions,
} from "@/lib/data/react-query/firm/firm-user";
import { type UserPage } from "@/lib/data/requests/firm/firm-user";
import { isEnabledDirectoryUser } from "@/lib/data/requests/firm/firm-user";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type DirectoryUser } from "@/lib/types";
import {
  type InfiniteData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

/** Search/paging state belongs to each picker; query pages are shared by search. */
export function useUserDirectory({
  enabled = true,
  selectedIds = [],
  open: controlledOpen,
  prioritizeSelectedUsers = true,
}: {
  enabled?: boolean;
  selectedIds?: string[];
  open?: boolean;
  prioritizeSelectedUsers?: boolean;
} = {}) {
  const [search, setSearch] = useState("");
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const debouncedSearch = useDebouncedValue(search.trim(), 250);
  const isOpen = controlledOpen ?? open;
  useEffect(() => {
    if (!isOpen) setSearch("");
  }, [isOpen]);
  const query = useInfiniteQuery(
    getInfiniteUserDirectoryOptions(debouncedSearch, enabled && isOpen),
  );
  const idsJson = JSON.stringify(
    [...new Set(selectedIds.filter(Boolean))].sort(),
  );
  const ids = useMemo<string[]>(
    () => JSON.parse(idsJson) as string[],
    [idsJson],
  );
  // A chosen user may be on another search's page. Keep its label while IDs rehydrate.
  const cachedSelected = useMemo(
    () =>
      queryClient
        .getQueriesData<InfiniteData<UserPage<DirectoryUser>>>({
          queryKey: [queryKeys.users, "directory", "infinite"],
        })
        .flatMap(([, data]) => data?.pages.flatMap((page) => page.data) ?? [])
        .concat(
          queryClient
            .getQueriesData<
              DirectoryUser[]
            >({ queryKey: [queryKeys.users, "directory", "ids"] })
            .flatMap(([, data]) => data ?? []),
        )
        .concat(query.data?.pages.flatMap((page) => page.data) ?? [])
        .filter((user) => ids.includes(user.id)),
    [ids, queryClient, query.data],
  );
  const selected = useQuery({
    ...getDirectoryUsersByIdsOptions(ids, enabled),
    placeholderData: cachedSelected.length ? cachedSelected : undefined,
  });
  const users = useMemo(() => {
    const pages =
      query.data?.pages.flatMap((page) =>
        page.data.filter(isEnabledDirectoryUser),
      ) ?? [];
    const usersInDisplayOrder = prioritizeSelectedUsers
      ? [...cachedSelected, ...pages, ...(selected.data ?? [])]
      : [...pages, ...cachedSelected, ...(selected.data ?? [])];
    return [
      ...new Map(usersInDisplayOrder.map((user) => [user.id, user])).values(),
    ];
  }, [query.data, selected.data, cachedSelected, prioritizeSelectedUsers]);
  const isLoading =
    enabled &&
    (selected.isLoading ||
      (isOpen && (query.isLoading || search.trim() !== debouncedSearch)));
  const isError = query.isError || selected.isError;
  const onLoadMore = () => {
    if (
      !isLoading &&
      query.hasNextPage &&
      !query.isFetchingNextPage &&
      !query.isFetchNextPageError
    )
      void query.fetchNextPage();
  };
  const onRetry = () => {
    if (selected.isError) void selected.refetch();
    if (query.isError)
      void (query.isFetchNextPageError
        ? query.fetchNextPage()
        : query.refetch());
  };
  return {
    users,
    search,
    setSearch,
    isLoading,
    isError,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
    selectProps: {
      search,
      onSearchChange: setSearch,
      onOpenChange: (nextOpen: boolean) => {
        setOpen(nextOpen);
        if (!nextOpen) setSearch("");
      },
      hasNextPage: query.hasNextPage,
      isFetchingNextPage: query.isFetchingNextPage,
      onLoadMore,
      onRetry,
      isItemsError: isError,
      isItemsLoading: isLoading,
      matchingValues:
        query.data?.pages.flatMap((page) =>
          page.data.filter(isEnabledDirectoryUser).map((user) => user.id),
        ) ?? [],
    },
  };
}
